import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { PrecastFigureSample, PrecastFigureObject } from '../thermal-resistivity-precast/thermal-resistivity-precast.component';

@Component({
    selector: 'app-precast-figure-ordering-popup',
    templateUrl: './figure-ordering-popup.component.html',
    styleUrls: ['./figure-ordering-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PrecastFigureOrderingPopupComponent implements OnInit {
  @Input() numberOfFigures = 0;
  @Input() figures: PrecastFigureObject[] = [];
  @Input() projectID = '';
  @Output() save = new EventEmitter<{
    figures: PrecastFigureObject[];
    reordered: boolean;
  }>();

  @Output() cancel = new EventEmitter<void>();

  private initialSignature = '';

  private initialFigures: PrecastFigureObject[] = [];

  loading = true;

  legendHeaderModalOpen = false;
  activeLegendIndex: number | null = null;
  legendHeaderDraft = '';

  ngOnInit(): void {
    this.initialFigures = JSON.parse(JSON.stringify(this.figures));
    this.initialSignature = this.computeOrderingSignature();
  }

  addFigureAfter(index: number): void {
    this.figures.splice(index + 1, 0, {
      maxX: null,
      maxY: null,
      legendHeader: null,
      figureNumber: index + 1,
      requirements: [],
      precastFigureSamples: [],
    });
  }

  removeFigure(index: number): void {
    if (this.figures[index]?.precastFigureSamples.length) return;
    this.figures.splice(index, 1);
  }

  onSave(): void {
    const figureOrdering: PrecastFigureObject[] = [];
    let k = 0;
    this.figures.forEach((figure) => {
      if (figure.precastFigureSamples.length > 0) {
        figureOrdering.push({
          maxX: null,
          maxY: null,
          figureNumber: k,
          legendHeader: figure.legendHeader,
          requirements: figure.requirements ?? [],
          precastFigureSamples: figure.precastFigureSamples,
        });
        k += 1;
      }
    });
    const reordered = this.computeOrderingSignature() !== this.initialSignature;
    this.save.emit({
      figures: figureOrdering,
      reordered,
    });
  }

  private computeOrderingSignature(): string {
    return this.figures
      .filter((figure) => figure.precastFigureSamples.length > 0)
      .map((figure) => figure.precastFigureSamples.map((curve) => curve.precastSampleID).join(','))
      .join('|');
  }

  onCancel(): void {
    this.figures.splice(0, this.figures.length, ...JSON.parse(JSON.stringify(this.initialFigures)));
    this.cancel.emit();
  }

  openLegendHeaderModal(index: number): void {
    this.activeLegendIndex = index;
    this.legendHeaderDraft = this.figures[index]?.legendHeader ?? '';
    this.legendHeaderModalOpen = true;
  }

  saveLegendHeader(): void {
    if (this.activeLegendIndex !== null) {
      this.figures[this.activeLegendIndex].legendHeader = this.legendHeaderDraft;
    }
    this.closeLegendHeaderModal();
  }

  closeLegendHeaderModal(): void {
    this.legendHeaderModalOpen = false;
    this.activeLegendIndex = null;
    this.legendHeaderDraft = '';
  }

  drop(event: CdkDragDrop<PrecastFigureSample[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }
}
